import useDynamicComponentBuilderStore from "%/stores/dynamic-component-builder-store";
import { mapActions, mapState } from "pinia";
import { useCollaboratorStore } from "~/stores/collaborator-store";
import {
  dragDropTracker,
} from "%/utils/interaction-timing-instrumentation";

const DragAndDropMixinStaging = {
  data() {
    return {
      isDraggingOver: false,
      isDragging: false,
    };
  },
  computed: {
    ...mapState(useDynamicComponentBuilderStore, [
      "rootElementIdentifier",
      "isDraggingOnBuilder",
      "currentlyInspectedElementIdentifiers",
      "isDynamicAppComponent",
    ]),
    ...mapState(useCollaboratorStore, [
      "canEditPublication",
    ]),
  },
  methods: {
    ...mapActions(useDynamicComponentBuilderStore, [
      "handleDragOverForLayout",
      "handleDragLeaveForElement",
      "handleDrop",
      "handleDragLeaveForLayout",
      "handleElementDragStart",
      "handleMultiElementDragStart",
      "handleDragEnd",
      "setElementToFocusOnHover",
      "isDraggable",
      "allowsDrop",
      "generateDragImageAndMousePosition",
      "generateMultiDragImageAndMousePosition",
    ]),
    internalHandleDragStart(event) {
      if (!this.isDynamicAppComponent) {
        if (!this.canEditPublication) {
          event.preventDefault();
          return;
        }

        if (!this.isDraggable || !this.identifier) {
          event.preventDefault();
          return;
        }
      }

      if (useDynamicComponentBuilderStore().isEditingElementInline(this.identifier)) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      if (this.currentlyInspectedElementIdentifiers.length > 1) {
        this.internalHandleMultiDrag(event);
        return;
      }

      // TIMING: Start drag operation tracking
      dragDropTracker.startDrag(this.identifier, false);

      this.isDragging = true;

      const {
        previewElement,
        yPositionOfMouseRelativeToElement,
        xPositionOfMouseRelativeToElement,
      } = this.generateDragImageAndMousePosition(event.target, event.clientX, event.clientY);

      // TIMING: Mark drag image generation complete
      dragDropTracker.markDragImageGenerated();

      this.$el.insertAdjacentElement("afterend", previewElement);

      event.dataTransfer.setDragImage(previewElement, xPositionOfMouseRelativeToElement, yPositionOfMouseRelativeToElement);
      event.dataTransfer.setData(
        "text/plain",
        JSON.stringify({ identifier: this.identifier, type: "element" }),
      );

      this.handleElementDragStart(this.identifier);
    },
    internalHandleMultiDrag(event) {
      dragDropTracker.startDrag(this.currentlyInspectedElementIdentifiers[0], true);
      const rootIdentifierIndex = this.currentlyInspectedElementIdentifiers.indexOf(this.rootElementIdentifier);
      if (rootIdentifierIndex > -1) {
        this.currentlyInspectedElementIdentifiers.splice(rootIdentifierIndex, 1);
      }

      const elementsBeingDragged = [];
      this.currentlyInspectedElementIdentifiers.forEach(id => {
        elementsBeingDragged.push(document.querySelector(`[data-identifier="${id}"]`));
      });

      const {
        previewElement,
        yPositionOfMouseRelativeToElement,
        xPositionOfMouseRelativeToElement,
      } = this.generateMultiDragImageAndMousePosition(event.target, elementsBeingDragged, event.clientX, event.clientY, true);

      this.$el.insertAdjacentElement("afterend", previewElement);
      event.dataTransfer.setDragImage(previewElement, xPositionOfMouseRelativeToElement, yPositionOfMouseRelativeToElement);

      this.dragging = true;
      event.dataTransfer.setData(
        "text/plain",
        JSON.stringify({ identifiers: this.currentlyInspectedElementIdentifiers, type: "element", isMultiDrag: true }),
      );

      this.handleMultiElementDragStart(this.currentlyInspectedElementIdentifiers);
      dragDropTracker.markDragImageGenerated();
    },
    internalHandleDragOver(event) {
      event.preventDefault();
      this.isDraggingOver = true;

      dragDropTracker.recordDragOver(this.identifier);

      if (!this.allowsDrop) {
        return;
      }

      if (this.isLayout) {
        this.handleDragOverForLayout({ event, identifier: this.identifier });
      } else {
        const parentId = this.dynamicComponentInstance.getElementParentId(this.identifier) || this.rootElementIdentifier;
        this.handleDragOverForLayout({ event, identifier: parentId, focusElements: false });
        if (this.isDraggingOnBuilder) {
          this.setElementToFocusOnHover(parentId);
        }
      }
    },
    internalHandleDragLeave(event) {
      event.preventDefault();
      this.isDraggingOver = false;

      if (!this.allowsDrop) {
        this.handleDragLeaveForElement({ event, identifier: this.identifier });
        return;
      }

      if (this.isLayout) {
        this.handleDragLeaveForLayout({ event, identifier: this.identifier });
      } else {
        this.handleDragLeaveForElement({ event, identifier: this.identifier });
      }
    },
    internalHandleDrop(event) {
      if (this.allowsDrop) {
        this.internalHandleDragLeave(event);
        if (this.isLayout) {
          this.handleDrop({ event, identifier: this.identifier });
        } else {
          const parentOfElementId = this.dynamicComponentInstance.getElementParentId(this.identifier);
          this.handleDrop({ event, identifier: parentOfElementId || this.rootElementIdentifier });
        }
      } else {
        this.internalHandleDragEnd();
      }
    },
    internalHandleDragEnd() {
      this.isDragging = false;
      this.isDraggingOver = false;
      document.querySelector("#dragging-clone")?.remove();

      this.handleDragEnd();
    },
  },
};

export { DragAndDropMixinStaging };
